import React from 'react';

/**
 * Interface for SVG icon props
 */
export interface IconProps {
  size?: number | string;
  color?: string;
  className?: string;
  style?: React.CSSProperties;
  onClick?: () => void;
}

/**
 * Creates a React component from an SVG string
 * @param svgString The SVG markup as a string
 * @returns A React component that renders the SVG
 */
export const createIconFromSvg = (svgString: string) => {
  return React.memo(({ size = 24, color, className, style, onClick, ...props }: IconProps) => {
    // Create a temporary div to parse the SVG string
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = svgString;
    
    // Find the SVG element
    const svgElement = tempDiv.querySelector('svg');
    
    if (!svgElement) {
      console.error('No SVG element found in the provided string');
      return null;
    }
    
    // Create a new SVG element with the desired attributes
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    
    // Copy attributes from the original SVG
    Array.from(svgElement.attributes).forEach(attr => {
      if (attr.name !== 'width' && attr.name !== 'height' && attr.name !== 'fill' && attr.name !== 'stroke') {
        svg.setAttribute(attr.name, attr.value);
      }
    });
    
    // Apply props
    svg.setAttribute('width', size.toString());
    svg.setAttribute('height', size.toString());
    
    // Set fill and stroke to currentColor if not already set
    if (!svgElement.hasAttribute('fill')) {
      svg.setAttribute('fill', 'currentColor');
    }
    
    if (!svgElement.hasAttribute('stroke')) {
      svg.setAttribute('stroke', 'currentColor');
    }
    
    // Override with color prop if provided
    if (color) {
      svg.setAttribute('fill', color);
      svg.setAttribute('stroke', color);
    }
    
    if (className) {
      svg.setAttribute('class', className);
    }
    
    // Copy the inner content (paths, etc.)
    svg.innerHTML = svgElement.innerHTML;
    
    // Apply color to all paths and other elements that might need it
    if (color) {
      const paths = svg.querySelectorAll('path, circle, rect, polygon, polyline, line');
      paths.forEach(path => {
        path.setAttribute('fill', color);
        path.setAttribute('stroke', color);
      });
    }
    
    // Convert to React element
    return React.createElement('span', {
      dangerouslySetInnerHTML: { __html: svg.outerHTML },
      onClick,
      style: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...style
      },
      ...props
    });
  });
};

/**
 * Loads an SVG file and returns it as a React component
 * @param path Path to the SVG file
 * @returns A React component that renders the SVG
 */
export const loadIcon = async (path: string) => {
  try {
    const response = await fetch(path);
    const svgText = await response.text();
    return createIconFromSvg(svgText);
  } catch (error) {
    console.error(`Failed to load icon from ${path}:`, error);
    return null;
  }
};

/**
 * Icon registry to cache loaded icons
 */
const iconRegistry: Record<string, React.FC<IconProps>> = {};

/**
 * Gets an icon by name from the registry or loads it if not already loaded
 * @param name Name of the icon
 * @returns A React component that renders the SVG
 */
export const getIcon = async (name: string): Promise<React.FC<IconProps> | null> => {
  // If icon is already in registry, return it
  if (iconRegistry[name]) {
    return iconRegistry[name];
  }
  
  // Use the correct path for production builds
  const isDevelopment = import.meta.env.DEV;
  
  // In development, use the src path. In production, use the assets path
  const iconPath = isDevelopment 
    ? `/src/assets/icons/${name}.svg`
    : `/assets/icons/${name}.svg`;
  
  try {
    const IconComponent = await loadIcon(iconPath);
    
    if (IconComponent) {
      iconRegistry[name] = IconComponent;
    }
    
    return IconComponent;
  } catch (error) {
    console.error(`Failed to load icon: ${name} from path: ${iconPath}`, error);
    return null;
  }
}; 